import { useState, useEffect, useRef } from 'react'
import { CheckCircle, AlertCircle, Loader, CreditCard, RefreshCw, XCircle } from 'lucide-react'
import { useBilling, PLANS } from '../hooks/useBilling'
import { formatNaira } from '../utils/formatters'

// Load Paystack inline JS once.
function usePaystackScript() {
  const [ready, setReady] = useState(!!window.PaystackPop)
  useEffect(() => {
    if (window.PaystackPop) return
    const script = document.createElement('script')
    script.src = 'https://js.paystack.co/v1/inline.js'
    script.async = true
    script.onload = () => setReady(true)
    document.body.appendChild(script)
    return () => document.body.removeChild(script)
  }, [])
  return ready
}

const STATUS_UI = {
  active:    { label: 'Active',     icon: CheckCircle,  color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200' },
  trialing:  { label: 'Trial',      icon: AlertCircle,  color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  past_due:  { label: 'Past due',   icon: AlertCircle,  color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200'   },
  cancelled: { label: 'Cancelled',  icon: AlertCircle,  color: 'text-gray-700',   bg: 'bg-gray-50',   border: 'border-gray-200'  },
}

export default function BillingPage({ currentUser, autoSubscribeTier, onAutoSubscribeStarted, onContinue }) {
  const scriptReady = usePaystackScript()
  const { billing, loading, refetch, initializePayment, cancelSubscription } = useBilling(currentUser)
  const [paying, setPaying] = useState(null) // tier string while popup is open
  const [successTier, setSuccessTier] = useState(null)
  const [pollCount, setPollCount] = useState(0)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState('')
  const [cancelPollCount, setCancelPollCount] = useState(0)

  // After popup success, poll for the webhook update (max 10 × 2s = 20s).
  useEffect(() => {
    if (!successTier) return
    if (billing?.subscription_status === 'active') { setSuccessTier(null); return }
    if (pollCount >= 10) return
    const t = setTimeout(() => { refetch(); setPollCount((c) => c + 1) }, 2000)
    return () => clearTimeout(t)
  }, [successTier, billing, pollCount, refetch])

  // After cancel, poll until webhook flips status to cancelled (max 10 × 2s).
  useEffect(() => {
    if (cancelPollCount === 0) return
    if (billing?.subscription_status === 'cancelled') { setCancelPollCount(0); return }
    if (cancelPollCount >= 10) return
    const t = setTimeout(() => { refetch(); setCancelPollCount((c) => c + 1) }, 2000)
    return () => clearTimeout(t)
  }, [cancelPollCount, billing, refetch])

  async function handleCancel() {
    setCancelling(true)
    setCancelError('')
    const result = await cancelSubscription()
    setCancelling(false)
    if (!result.success) {
      setCancelError(result.message)
      return
    }
    setShowCancelConfirm(false)
    setCancelPollCount(1)
  }

  async function subscribe(plan) {
    if (!scriptReady) return
    setPaying(plan.tier)
    const result = await initializePayment({ plan_code: plan.plan_code, email: currentUser.email, amount: plan.amount })
    setPaying(null)
    if (result.success) {
      setSuccessTier(plan.tier)
      setPollCount(0)
    }
  }

  const status = billing?.subscription_status ?? 'trialing'
  const statusUI = STATUS_UI[status] ?? STATUS_UI.trialing
  const StatusIcon = statusUI.icon

  // Pay-immediately deep-link: once billing has loaded and the Paystack script
  // is ready, auto-open the popup for the chosen tier (fires exactly once).
  const autoFiredRef = useRef(false)
  useEffect(() => {
    if (autoFiredRef.current || !autoSubscribeTier || !scriptReady || loading) return
    if (status === 'active') return
    const plan = PLANS.find((p) => p.tier === autoSubscribeTier)
    if (!plan) return
    autoFiredRef.current = true
    onAutoSubscribeStarted?.()
    subscribe(plan)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubscribeTier, scriptReady, loading, status])

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-2">
      {/* Cancel confirm dialog */}
      {showCancelConfirm && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/50" onClick={() => !cancelling && setShowCancelConfirm(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[400px] z-[70] bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">Cancel subscription?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Your plan will remain active until the end of the current billing period, then your account will revert to trial status.
                </p>
              </div>
            </div>
            {cancelError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{cancelError}</p>
            )}
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => { setShowCancelConfirm(false); setCancelError('') }}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Keep plan
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {cancelling ? <><Loader className="w-4 h-4 animate-spin" /><span>Cancelling…</span></> : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Billing</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your PetroTrack subscription</p>
      </div>

      {/* Current status card */}
      {!loading && (
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${statusUI.bg} ${statusUI.border}`}>
          <StatusIcon className={`w-5 h-5 flex-shrink-0 ${statusUI.color}`} />
          <div className="flex-1">
            <p className={`text-sm font-semibold ${statusUI.color}`}>
              {statusUI.label}
              {billing?.plan && ` — ${billing.plan.charAt(0).toUpperCase() + billing.plan.slice(1)} plan`}
            </p>
            {billing?.current_period_end && status === 'active' && (
              <p className="text-xs text-gray-500 mt-0.5">
                Renews {new Date(billing.current_period_end).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {status === 'active' && (
              <button
                onClick={() => { setShowCancelConfirm(true); setCancelError('') }}
                className="text-xs font-medium text-red-500 hover:text-red-700 transition"
              >
                Cancel plan
              </button>
            )}
            <button onClick={refetch} className="text-gray-400 hover:text-gray-600 transition">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Cancellation pending banner */}
      {cancelPollCount > 0 && billing?.subscription_status !== 'cancelled' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
          <Loader className="w-5 h-5 text-amber-600 flex-shrink-0 animate-spin" />
          <div>
            <p className="text-sm font-semibold text-amber-700">Cancellation submitted — updating status…</p>
            <p className="text-xs text-amber-600 mt-0.5">This usually takes a few seconds. Refresh if it doesn't update.</p>
          </div>
        </div>
      )}

      {/* Success banner (while webhook is propagating) */}
      {successTier && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-700">Payment received — activating your plan…</p>
            <p className="text-xs text-green-600 mt-0.5">This usually takes a few seconds. Refresh if it doesn't update.</p>
          </div>
        </div>
      )}

      {/* Plan picker (hide if already active) */}
      {status !== 'active' && (
        <>
          {/* Plans grid */}
          <div className="grid gap-4">
            {PLANS.map((plan) => {
              const isCurrentPlan = billing?.plan === plan.tier
              const isPaying = paying === plan.tier
              const isGrowth = plan.tier === 'growth'

              return (
                <div
                  key={plan.tier}
                  className={`rounded-xl border-2 p-5 transition ${
                    isGrowth ? 'border-blue-600 bg-blue-50/30' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-800 text-base">{plan.name}</h3>
                        {isGrowth && (
                          <span className="text-xs font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">Popular</span>
                        )}
                        {isCurrentPlan && (
                          <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Current</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-bold text-gray-800">{formatNaira(plan.amount)}</p>
                      <p className="text-xs text-gray-400">/month</p>
                    </div>
                  </div>

                  <button
                    onClick={() => subscribe(plan)}
                    disabled={!!paying || !scriptReady || isCurrentPlan}
                    className={`mt-4 w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 ${
                      isGrowth
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-800 text-white hover:bg-gray-900'
                    }`}
                  >
                    {isPaying ? (
                      <><Loader className="w-4 h-4 animate-spin" /><span>Opening…</span></>
                    ) : (
                      <><CreditCard className="w-4 h-4" /><span>{isCurrentPlan ? 'Current plan' : 'Subscribe'}</span></>
                    )}
                  </button>
                </div>
              )
            })}
          </div>

          <p className="text-center text-xs text-gray-400">
            Payments processed securely by Paystack. NGN only. Cancel anytime.
          </p>
        </>
      )}

      {onContinue && (
        <div className="text-center pt-1">
          <button
            onClick={onContinue}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 transition"
          >
            {status === 'active' ? 'Continue to PetroTrack →' : 'Skip for now →'}
          </button>
        </div>
      )}
    </div>
  )
}
